/**
 * Builds public share links.
 *
 * Domains come from env so links can be hosted on neutral "blind" domains with
 * no visible affiliation to the platform. Each share kind can have its own
 * domain (catalog.* vs custom.*), falling back to a single shared domain.
 */
const FALLBACK_DOMAIN = 'https://app.univdiam.com'

/** Per-kind override. Falls back to VITE_SHARE_DOMAIN when a kind has no domain of its own. */
const DOMAIN_ENV_BY_KIND = {
  share_product: 'VITE_SHARE_DOMAIN_CUSTOM',
  share_catalog: 'VITE_SHARE_DOMAIN_CATALOG',
} as const

export type ShareKind = keyof typeof DOMAIN_ENV_BY_KIND

/** Path-safe slug. Business names contain `/`, `&`, commas — `/` would add a path segment. */
export const businessSlug = (businessName?: string | null) =>
  businessName
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'business'

export const shareUrl = (
  kind: ShareKind,
  shareCode: string,
  businessName?: string | null
) => {
  // packages/ui has no vite/client types (it is the only env read in this package),
  // so widen import.meta here rather than augment ImportMeta globally and clash with the apps.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env

  // `||` not `??`: a defined-but-empty env var is common in CI, and `??` would
  // let it through and produce a protocol-less relative URL.
  const domain = (
    env?.[DOMAIN_ENV_BY_KIND[kind]] || env?.VITE_SHARE_DOMAIN || FALLBACK_DOMAIN
  ).replace(/\/+$/, '')

  return `${domain}/${businessSlug(businessName)}/${kind}/${shareCode}`
}
